import React, { useEffect, useState } from 'react';
import pt from 'prop-types';
import cn from 'classnames';
import PreviewImage from '../../PreviewImage';
import { formattedSeason } from '../../../utils/functions';
import styles from './PastImagesQuickOrder.module.scss';

function formatPrice(option) {
  if (!option) return '';
  const priceRecord = option.prices && option.prices[0];
  const price = priceRecord && priceRecord.price;
  if (price == null) return '';
  return `$${Number(price).toFixed(2)}`;
}

// formattedSeason() returns garbage like " 0" when the folder is empty or
// purely numeric (some legacy student_image rows have no season prefix).
// Fall back through several signals so every tile gets a meaningful label.
function displaySeasonLabel(studentImage) {
  const folder = (studentImage.folder || '').trim();
  const rawFormatted = folder ? formattedSeason(folder).trim() : '';

  if (/^[A-Za-z]+\s+\d{4}$/.test(rawFormatted)) return rawFormatted;

  const yearMatch = folder.match(/(\d{4})/);
  const wordMatch = folder.replace(/\d/g, '').trim();

  if (yearMatch && wordMatch) {
    const displayYear = wordMatch.toLowerCase().includes('fall')
      ? parseInt(yearMatch[1], 10) - 1
      : parseInt(yearMatch[1], 10);
    return `${wordMatch.charAt(0).toUpperCase()}${wordMatch.slice(1)} ${displayYear}`;
  }

  if (yearMatch) return `Year ${yearMatch[1]}`;
  if (wordMatch) return `${wordMatch.charAt(0).toUpperCase()}${wordMatch.slice(1)}`;

  if (studentImage.year) return `Year ${studentImage.year}`;
  return 'Past Picture';
}

export default function PastImageOption({
  studentImage,
  options,
  backgrounds,
  defaultBackground,
  selection,
  onSelectOption,
  onSelectBackground,
  packagePricelist,
  onViewPackages,
}) {
  const watermark = studentImage.watermarkUrl || studentImage.imageUrl;
  const selectedOptionId = selection?.optionId || null;
  const selectedBackgroundId = selection?.backgroundId || defaultBackground?.id || null;
  const previewBackground = backgrounds.find((bg) => bg.id === selectedBackgroundId)
    || defaultBackground
    || backgrounds[0]
    || null;

  // Background picker open by default after an option is selected. Once the
  // customer is happy with a background, they can collapse it to declutter
  // the tile and reopen via "Change Background".
  const [isBackgroundOpen, setIsBackgroundOpen] = useState(true);

  useEffect(() => {
    if (selectedOptionId) setIsBackgroundOpen(true);
  }, [selectedOptionId]);

  return (
    <div className={styles.tile}>
      <div className={styles.preview}>
        <PreviewImage
          foregroundUrl={watermark}
          backgroundUrl={previewBackground?.imageUrl || ''}
          isPreviewHidden={false}
        />
      </div>
      <p className={styles.season}>{displaySeasonLabel(studentImage)}</p>

      <div className={styles.optionsList}>
        {options.map((opt) => {
          const isSelected = selectedOptionId === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={cn(styles.optionBtn, { [styles.optionBtnSelected]: isSelected })}
              onClick={() => onSelectOption(studentImage.id, isSelected ? null : opt.id)}
              aria-pressed={isSelected}
            >
              <span className={styles.optionLeft}>
                <span
                  className={cn(styles.optionCheckbox, { [styles.optionCheckboxChecked]: isSelected })}
                  aria-hidden="true"
                >
                  {isSelected ? '✓' : ''}
                </span>
                <span>{opt.name}</span>
              </span>
              <span className={styles.optionPrice}>{formatPrice(opt)}</span>
            </button>
          );
        })}
      </div>

      {/* Directly under the option list, on every tile: the three Quick Order
          options are single items, and a parent who wants an actual package for
          this year's photo needs a way through to the full pricelist. Rendered
          only when a past-images pricelist covers this photo's season -- no
          pricelist, no link, rather than a dead end. */}
      {packagePricelist && (
        <button
          type="button"
          className={styles.viewPackagesLink}
          onClick={() => onViewPackages(studentImage, packagePricelist)}
        >
          View Packages
        </button>
      )}

      {selectedOptionId && backgrounds.length > 0 && (
        isBackgroundOpen ? (
          <div className={styles.bgRow}>
            <div className={styles.bgHeader}>
              <span className={styles.bgLabel}>Background</span>
              <button
                type="button"
                className={styles.bgHideLink}
                onClick={() => setIsBackgroundOpen(false)}
              >
                Hide
              </button>
              <button
                type="button"
                className={styles.bgHideClose}
                aria-label="Hide backgrounds"
                onClick={() => setIsBackgroundOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.bgList}>
              {backgrounds.map((bg) => {
                const isSelected = selectedBackgroundId === bg.id;
                return (
                  <button
                    key={bg.id}
                    type="button"
                    aria-label={`Background ${bg.name || bg.letter || bg.id}`}
                    title={bg.name || bg.letter || ''}
                    className={cn(styles.bgSwatch, { [styles.bgSwatchSelected]: isSelected })}
                    style={{ backgroundImage: `url(${bg.imageUrl})` }}
                    onClick={() => onSelectBackground(studentImage.id, bg.id)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.bgShowLink}
            onClick={() => setIsBackgroundOpen(true)}
          >
            Change Background
          </button>
        )
      )}
    </div>
  );
}

PastImageOption.defaultProps = {
  selection: null,
  backgrounds: [],
  defaultBackground: null,
  packagePricelist: null,
  onViewPackages: () => {},
};

PastImageOption.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  studentImage: pt.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  options: pt.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  backgrounds: pt.array,
  // eslint-disable-next-line react/forbid-prop-types
  defaultBackground: pt.object,
  // eslint-disable-next-line react/forbid-prop-types
  selection: pt.object,
  onSelectOption: pt.func.isRequired,
  onSelectBackground: pt.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  packagePricelist: pt.object,
  onViewPackages: pt.func,
};
